import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingList as ShoppingListType, ShoppingItem } from '../types';
import ItemCard from '../components/ItemCard';
import AddItemForm from '../components/AddItemForm';
import ShareListModal from '../components/ShareListModal';
import { ArrowLeft, Plus, Share2, ShoppingCart } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { api } from '../utils/api';

const ShoppingListPage: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState<ShoppingListType | null>(null);
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const hasFetched = useRef(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchListDetails = useCallback(async () => {
    if (!listId || !user) return;
    
    try {
      const response = await api.get(`/lists/${listId}`);
      
      if (response.ok) {
        const listData = await response.json();
        setList(listData);
      } else {
        console.error('Failed to fetch list details:', response.status);
        // Fallback to a default name if the API fails
        setList({
          id: listId,
          name: 'Shopping List',
          created_by: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error('Error fetching list details:', error);
      // Fallback to a default name if there's an error
      setList({
        id: listId,
        name: 'Shopping List',
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  }, [listId, user]);

  const fetchItems = useCallback(async () => {
    if (!listId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/lists/${listId}/items`);
      
      if (response.ok) {
        const data = await response.json();
        setItems(data || []);
      } else {
        console.error('Failed to fetch items:', response.status);
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => {
    console.log('ShoppingList useEffect triggered:', { listId, user: !!user, hasFetched: hasFetched.current });
    
    if (listId && user && !hasFetched.current) {
      console.log('Calling fetchListDetails and fetchItems');
      hasFetched.current = true;
      fetchListDetails();
      fetchItems();
    } else if (!listId || !user) {
      console.log('Missing listId or user, setting loading to false');
      setLoading(false);
    }
  }, [listId, user, fetchListDetails, fetchItems]);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.warn('Loading timeout - setting loading to false');
        setLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  const addItem = async (name: string, quantity: number) => {
    if (!user) return;

    try {
      const response = await api.post(`/lists/${listId}/items`, {
        name,
        quantity,
      });

      if (response.ok) {
        const newItem = await response.json();
        setItems([...items, newItem]);
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const toggleItem = async (itemId: string, checked: boolean) => {
    if (!user) return;

    // Store the original item state for potential rollback
    const originalItem = items.find(item => item.id === itemId);
    if (!originalItem) return;

    // Optimistic update - update UI immediately
    setItems(items.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            checked,
            checked_by: checked ? user.id : undefined,
            checked_at: checked ? new Date().toISOString() : undefined
          }
        : item
    ));

    try {
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 5000);
      });

      // Create the fetch promise
      const fetchPromise = api.patch(`/items/${itemId}`, {
        checked,
      });

      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]) as Response;

      if (response.ok) {
        const updatedItem = await response.json();
        // Update with server response to ensure consistency
        setItems(items.map(item => 
          item.id === itemId ? updatedItem : item
        ));
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating item:', error);
      
      // Revert the optimistic update on failure
      setItems(items.map(item => 
        item.id === itemId ? originalItem : item
      ));

      // Show error message to user
      alert(`Failed to ${checked ? 'check' : 'uncheck'} item. Please try again.`);
    }
  };

  const updateItem = async (itemId: string, name: string, quantity: number) => {
    if (!user) return;

    // Store the original item state for potential rollback
    const originalItem = items.find(item => item.id === itemId);
    if (!originalItem) return;

    // Optimistic update - update UI immediately
    setItems(items.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            name,
            quantity,
            updated_at: new Date().toISOString()
          }
        : item
    ));

    try {
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 5000);
      });

      // Create the fetch promise
      const fetchPromise = api.patch(`/items/${itemId}`, {
        name,
        quantity,
      });

      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]) as Response;

      if (response.ok) {
        const updatedItem = await response.json();
        // Update with server response to ensure consistency
        setItems(items.map(item => 
          item.id === itemId ? updatedItem : item
        ));
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating item:', error);
      
      // Revert the optimistic update on failure
      setItems(items.map(item => 
        item.id === itemId ? originalItem : item
      ));

      // Show error message to user
      alert('Failed to update item. Please try again.');
    }
  };

  const shareList = async (email: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const response = await api.post(`/lists/${listId}/share`, {
        email,
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        throw new Error('Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sharing list:', error);
      throw error;
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update the UI
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    try {
      // Update sort orders on the server
      const reorderData = newItems.map((item, index) => ({
        id: item.id,
        sort_order: (index + 1) * 1000, // Use increments of 1000 for easy reordering
      }));

      const response = await api.patch(`/lists/${listId}/reorder`, { items: reorderData });

      if (!response.ok) {
        throw new Error('Failed to reorder items');
      }
    } catch (error) {
      console.error('Error reordering items:', error);
      // Revert the optimistic update on failure
      setItems(items);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading list...</p>
        </div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">List not found</h2>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to My Lists
          </button>
        </div>
      </div>
    );
  }

  const checkedItems = items.filter(item => item.checked);
  const uncheckedItems = items.filter(item => !item.checked);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ShoppingCart className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{list.name}</h1>
                  <p className="text-sm text-gray-500">
                    {items.length} items • {checkedItems.length} completed
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Item Section */}
        {showAddForm && listId ? (
          <AddItemForm
            onAddItem={addItem}
            onCancel={() => setShowAddForm(false)}
            listId={listId}
          />
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full flex items-center justify-center px-6 py-4 bg-white border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors mb-8"
          >
            <Plus className="w-5 h-5 mr-2 text-gray-400" />
            <span className="text-gray-600">Add new item</span>
          </button>
        )}

        {/* Items List */}
        {items.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No items yet</h3>
            <p className="text-gray-600 mb-6">Add your first item to get started</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-6">
              {/* Unchecked Items */}
              {uncheckedItems.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    To Buy ({uncheckedItems.length})
                  </h2>
                  <SortableContext
                    items={uncheckedItems.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {uncheckedItems.map((item) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          onToggle={toggleItem}
                          onUpdate={updateItem}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </div>
              )}

              {/* Checked Items */}
              {checkedItems.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Completed ({checkedItems.length})
                  </h2>
                  <SortableContext
                    items={checkedItems.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {checkedItems.map((item) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          onToggle={toggleItem}
                          onUpdate={updateItem}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </div>
              )}
            </div>
          </DndContext>
        )}
      </main>

      {/* Share Modal */}
      {listId && (
        <ShareListModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          onShare={shareList}
          listName={list.name}
          listId={listId}
          currentUserId={user?.id}
        />
      )}
    </div>
  );
};

export default ShoppingListPage;
